/** QuickPlan — Plan de coupe des panneaux (calepinage), à remettre à un service de découpe.
 *
 * Pièces concernées : composants d'un seul pavé, minces (épaisseur ≤ 50 mm et nettement
 * plus petite que les deux autres côtés) — côtés, tablettes, fonds, portes, plateaux…
 * Elles sont regroupées par matière + épaisseur, puis rangées dans les formats de
 * panneaux ou planches du fournisseur (settings.sheets) ; à défaut, un format courant
 * 2800 × 2070. Rognage des bords par format (0 pour une planche aux chants finis).
 *
 * Rangement GUILLOTINE (chaque coupe traverse le morceau de bord à bord, comme sur une
 * scie à panneaux de magasin) : choix du meilleur emplacement (plus petit reste sur le
 * côté court), plusieurs ordres de tri et règles de découpe essayés, le meilleur gardé.
 * Trait de scie entre pièces, rognage des bords abîmés du panneau. Nombre de traits de
 * scie (un par partage d'un morceau, + 4 de rognage) et longueur de coupe par panneau.
 */
import { resolveComponent } from './resolve.js';

const r1 = (v) => Math.round(v * 10) / 10;
const EPS = 1e-6;

/** Formats courants proposés à la saisie (mm). */
export const SHEET_PRESETS = [
  { label: 'Aggloméré / mélaminé 2800 × 2070', length: 2800, width: 2070 },
  { label: 'Panneau 2500 × 1250', length: 2500, width: 1250 },
  { label: 'Panneau 2440 × 1220', length: 2440, width: 1220 },
  { label: 'Contreplaqué 2500 × 1220', length: 2500, width: 1220 },
  { label: 'Demi-panneau 1250 × 1220', length: 1250, width: 1220 },
  { label: 'Planche / tablette 2500 × 500 (chants finis)', length: 2500, width: 500, trim: 0 },
  { label: 'Planche / tablette 2500 × 600 (chants finis)', length: 2500, width: 600, trim: 0 },
  { label: 'Planche / tablette 2000 × 400 (chants finis)', length: 2000, width: 400, trim: 0 },
];
export const DEFAULT_SHEET = { length: 2800, width: 2070 };

/** « 12 traits de scie (dont 4 de rognage), 9,4 m » — total comprenant le rognage. */
export function sawCutsLabel(total, trimCuts = 0, lengthMm = null) {
  const s = `${total} trait${total > 1 ? 's' : ''} de scie${trimCuts ? ` (dont ${trimCuts} de rognage)` : ''}`;
  return lengthMm != null && total ? `${s}, ${String(Math.round(lengthMm / 100) / 10).replace('.', ',')} m` : s;
}

/** Dimensions de découpe d'une instance si c'est un panneau : { L, W, T } (L ≥ W), sinon null. */
export function panelDims(comp, res) {
  if (comp.category === 'quincaillerie' || comp.category === 'rack') return null;
  if ((comp.bom?.kind ?? 'piece') !== 'piece' || comp.sheet === false) return null;
  if (res.parts.length !== 1 || res.parts[0].type !== 'box') return null;
  const s = [...res.parts[0].size].sort((a, b) => a - b);
  if (s[0] > 50 || s[1] < 2 * s[0]) return null;
  return { T: r1(s[0]), W: r1(s[1]), L: r1(s[2]) };
}

const norm = (s) => String(s ?? '').trim().toLowerCase();

/** Pièces à découper du projet (une par instance). letterOf : repères de la notice. */
export function panelPieces(project, lookup, letterOf = {}) {
  const out = [];
  for (const inst of project.instances ?? []) {
    const comp = lookup(inst.componentId);
    if (!comp) continue;
    let res;
    try { res = resolveComponent(comp, inst.params); } catch { continue; }
    const d = panelDims(comp, res);
    if (!d) continue;
    out.push({ instId: inst.id, letter: letterOf[inst.id] ?? '', name: inst.name || comp.name, material: comp.material || 'Panneau', ...d });
  }
  return out;
}

// ── Rangement guillotine ─────────────────────────────────────────

function packOnce(items, U, kerf, allowRot, splitRule) {
  const sheets = [];
  const oversize = [];
  const fits = (it, f) => {
    const opts = [];
    if (it.L <= f.w + EPS && it.W <= f.h + EPS) opts.push([it.L, it.W, false]);
    if (allowRot && it.W <= f.w + EPS && it.L <= f.h + EPS && it.L !== it.W) opts.push([it.W, it.L, true]);
    return opts;
  };
  for (const it of items) {
    let best = null;
    const consider = (sheet, fi) => {
      const f = sheet.free[fi];
      for (const [w, h, rot] of fits(it, f)) {
        const a = Math.min(f.w - w, f.h - h);
        const b = Math.max(f.w - w, f.h - h);
        if (!best || a < best.a - EPS || (Math.abs(a - best.a) < EPS && b < best.b)) best = { sheet, fi, w, h, rot, a, b };
      }
    };
    for (const s of sheets) for (let i = 0; i < s.free.length; i++) consider(s, i);
    if (!best) {
      const s = { free: [{ x: 0, y: 0, w: U.w, h: U.h }], placed: [], cuts: 0, cutLength: 0 };
      consider(s, 0);
      if (!best) { oversize.push(it); continue; }
      sheets.push(s);
    }
    const { sheet, fi, w, h, rot } = best;
    const f = sheet.free[fi];
    sheet.free.splice(fi, 1);
    sheet.placed.push({ ...it, x: f.x, y: f.y, w, h, rot });
    const rw = f.w - w - kerf;
    const rh = f.h - h - kerf;
    // Coupe horizontale (le long de w) ou verticale selon la règle : reste le plus utile.
    const horizontal = splitRule === 'short' ? f.w - w < f.h - h : f.w - w >= f.h - h;
    const parts = horizontal
      ? [{ x: f.x + w + kerf, y: f.y, w: rw, h }, { x: f.x, y: f.y + h + kerf, w: f.w, h: rh }]
      : [{ x: f.x + w + kerf, y: f.y, w: rw, h: f.h }, { x: f.x, y: f.y + h + kerf, w, h: rh }];
    for (const p of parts) if (p.w > 1 && p.h > 1) sheet.free.push(p);
    // Traits de scie : chaque partage d'un morceau en deux en est un (aucun si la pièce
    // remplit déjà le morceau dans ce sens). Longueur = traversée complète du morceau.
    const cutW = f.w - w > EPS; // trait parallèle à la hauteur, à droite de la pièce
    const cutH = f.h - h > EPS; // trait parallèle à la longueur, au-dessus de la pièce
    if (horizontal) {
      if (cutH) { sheet.cuts++; sheet.cutLength += f.w; }
      if (cutW) { sheet.cuts++; sheet.cutLength += h; }
    } else {
      if (cutW) { sheet.cuts++; sheet.cutLength += f.h; }
      if (cutH) { sheet.cuts++; sheet.cutLength += w; }
    }
  }
  return { sheets, oversize };
}

const ORDERS = [
  (a, b) => b.L * b.W - a.L * a.W || b.L - a.L,
  (a, b) => b.L - a.L || b.W - a.W,
  (a, b) => b.W - a.W || b.L - a.L,
  (a, b) => b.L + b.W - (a.L + a.W) || b.L - a.L,
];

/** Range des pièces dans un format : meilleur des essais (moins de panneaux, moins de
 *  traits de scie, puis plus grande chute d'un seul tenant sur le dernier panneau). */
export function packSheets(pieces, sheet, { kerf = 4, trim = 10 } = {}) {
  const U = { w: sheet.length - 2 * trim, h: sheet.width - 2 * trim };
  let best = null;
  for (const order of ORDERS) {
    for (const rule of ['short', 'long']) {
      const r = packOnce([...pieces].sort(order), U, kerf, !sheet.grain, rule);
      const last = r.sheets[r.sheets.length - 1];
      const lastFree = last ? Math.max(0, ...last.free.map((f) => f.w * f.h)) : 0;
      // Moins de panneaux, puis moins de traits de scie, puis plus grande chute d'un tenant.
      const score = [r.sheets.length, r.sheets.reduce((a, x) => a + x.cuts, 0), -lastFree];
      const k = best && score.findIndex((v, i) => v !== best.score[i]);
      if (!best || (k >= 0 && score[k] < best.score[k])) best = { ...r, score };
    }
  }
  const full = sheet.length * sheet.width;
  const sheets = best.sheets.map((s, i) => {
    const used = s.placed.reduce((a, p) => a + p.w * p.h, 0);
    const offcuts = s.free.filter((f) => f.w >= 100 && f.h >= 100).sort((a, b) => b.w * b.h - a.w * a.h);
    return {
      index: i + 1,
      placements: s.placed.map((p) => ({ ...p, x: p.x + trim, y: p.y + trim })),
      offcuts: offcuts.map((f) => ({ ...f, x: f.x + trim, y: f.y + trim })),
      usage: Math.round((1000 * used) / full) / 10,
      cuts: s.cuts,
      trimCuts: trim > 0 ? 4 : 0,
      cutLength: Math.round(s.cutLength + (trim > 0 ? 2 * (sheet.length + sheet.width) : 0)),
    };
  });
  const area = pieces.filter((p) => !best.oversize.includes(p)).reduce((a, p) => a + p.L * p.W, 0);
  return {
    sheets,
    oversize: best.oversize,
    usage: sheets.length ? Math.round((1000 * area) / (sheets.length * full)) / 10 : 0,
    cuts: sheets.reduce((a, x) => a + x.cuts + x.trimCuts, 0),
    trimCuts: sheets.reduce((a, x) => a + x.trimCuts, 0),
    cutLength: sheets.reduce((a, x) => a + x.cutLength, 0),
  };
}

/** Plan de coupe complet : un groupe par matière + épaisseur, avec le format retenu. */
export function computeCutPlan(project, lookup, letterOf = {}) {
  const s = project.settings ?? {};
  const kerf = s.sheetKerf ?? 4;
  const trim = s.sheetTrim ?? 10;
  const stock = s.sheets ?? [];
  const pieces = panelPieces(project, lookup, letterOf);
  const groups = new Map();
  for (const p of pieces) {
    const key = `${norm(p.material)}|${p.T}`;
    if (!groups.has(key)) groups.set(key, { key, material: p.material, thickness: p.T, pieces: [] });
    groups.get(key).pieces.push(p);
  }

  const out = [];
  for (const g of groups.values()) {
    // Formats possibles : même épaisseur, et même matière (ou matière non précisée).
    const exact = stock.filter((x) => Math.abs(x.thickness - g.thickness) < 0.05 && norm(x.material) === norm(g.material));
    const anyMat = stock.filter((x) => Math.abs(x.thickness - g.thickness) < 0.05 && !norm(x.material));
    const candidates = exact.length ? exact : anyMat;
    const isDefault = candidates.length === 0;
    const formats = isDefault ? [{ id: 'defaut', material: '', thickness: g.thickness, ...DEFAULT_SHEET, grain: false }] : candidates;

    let best = null;
    for (const f of formats) {
      const r = packSheets(g.pieces, f, { kerf, trim: f.trim ?? trim });
      const cost = f.price != null ? r.sheets.length * f.price : null;
      const area = r.sheets.length * f.length * f.width;
      // Le moins cher si tous les formats ont un prix, sinon la plus petite surface achetée.
      const better = !best
        || r.oversize.length < best.oversize.length
        || (r.oversize.length === best.oversize.length && (cost != null && best.cost != null ? cost < best.cost : area < best.area));
      if (better) best = { ...r, format: { ...f, trim: f.trim ?? trim }, cost, area };
    }

    // Liste de débit : pièces identiques regroupées.
    const rows = new Map();
    for (const p of g.pieces) {
      const k = `${p.letter}|${p.L}|${p.W}`;
      const row = rows.get(k) ?? { letter: p.letter, name: p.name, L: p.L, W: p.W, qty: 0 };
      row.qty++;
      rows.set(k, row);
    }
    out.push({
      key: g.key,
      material: g.material,
      thickness: g.thickness,
      format: best.format,
      defaultFormat: isDefault,
      sheets: best.sheets,
      oversize: best.oversize,
      usage: best.usage,
      cost: best.cost,
      list: [...rows.values()].sort((a, b) => b.L - a.L || b.W - a.W),
      count: g.pieces.length,
      cuts: best.cuts,
      trimCuts: best.trimCuts,
      cutLength: best.cutLength,
    });
  }
  out.sort((a, b) => a.material.localeCompare(b.material, 'fr') || b.thickness - a.thickness);
  return {
    groups: out,
    kerf,
    trim,
    cuts: out.reduce((a, g) => a + g.cuts, 0),
    trimCuts: out.reduce((a, g) => a + g.trimCuts, 0),
    cutLength: out.reduce((a, g) => a + g.cutLength, 0),
    total: out.reduce((a, g) => a + (g.cost ?? 0), 0),
    unpriced: out.some((g) => g.cost == null),
  };
}
